<template>
  <div v-if="!hide_on_no_results || products.length > 0">
    <div class="swiper-title text-uppercase text-center">
      <div class="strike">
        <span class="font-weight-bold text-wrap px-4">{{ title }}</span>
      </div>
      <span v-if="seeAllLink" class="link">
        <router-link :to="seeAllLink">
          {{ $t('products_slider.see_all') }}
        </router-link>
      </span>
    </div>

    <!-- swiper -->
    <vue-element-loading :active="loading_products" />
    <b-link
      v-if="$refs.productsSwiper"
      :disabled="$refs.productsSwiper.swiper.isBeginning"
      class="mt-4 slider-button slider-prev"
      @click="$refs.productsSwiper.swiper.slidePrev()"
    >
      <img src="@/static/carousel_left.png" class="m-0 p-0" />
    </b-link>
    <swiper
      ref="productsSwiper"
      :options="swiperOption"
      @reachEnd="onSwipeReachEnd"
    >
      <div
        v-if="products.length == 0"
        class="slider-empty"
        v-bind:class="{'slider-empty-compact': (type=='compact')}"
      >
        <div v-if="!loading_products">
          {{ $t('products_slider.no_results') }}
        </div>
      </div>
      <swiper-slide
        v-for="(product, index) in products"
        :key="index"
        class="h-auto sw-slide"
        v-bind:class="{'sw-slide-compact': (type=='compact')}"
      >
        <ProductCardCompact
          v-if="type === 'compact'"
          :product="product"
        />
        <ProductCard
          v-else
          :product="product"
        />
      </swiper-slide>
    </swiper>
    <b-link
      v-if="$refs.productsSwiper"
      :disabled="$refs.productsSwiper.swiper.isEnd"
      class="mt-4 slider-button slider-next"
      @click="$refs.productsSwiper.swiper.slideNext()"
    >
      <img src="@/static/carousel_right.png" />
    </b-link>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import ProductCard from "@/components/ProductCard";
import ProductCardCompact from "@/components/ProductCardCompact";

export default {
  components: {
    ProductCard,
    ProductCardCompact,
  },
  props: {
    title: String,
    seeAllLink: Object,
    source: String,
    hide_on_no_results: {
      type: Boolean,
      default: false,
    },
    payload: {
      type: Object,
      default: () => {},
    },
    type: {
      type: String,
      default: "standard",
    },
    name: String,
  },
  data() {
    return {
      uid: Math.random().toString(),
      loading_products: false,
      per_page: 8,
      swiperOption: {
        slidesPerView: 1,
        spaceBetween: 0,
        breakpointsInverse: true,
        breakpoints: {
          // when window width is >= 320px
          320: {
            slidesPerView: 2,
            spaceBetween: 2,
          },
          // when window width is >= 480px
          480: {
            slidesPerView: 2,
            spaceBetween: 10,
          },
          667: {
            slidesPerView: 3,
            spaceBetween: 10,
          },
          // when window width is >= 640px
          768: {
            slidesPerView: this.type === "compact" ? 4 : 3,
            spaceBetween: 30,
          },
          1000: {
            slidesPerView: this.type === "compact" ? 6 : 4,
            spaceBetween: 30,
          },
        },
      },
    };
  },
  computed: {
    ...mapGetters("drugs", ["getSlider"]),
    products() {
      return this.getSlider(this.uid).products;
    },
    productsCount() {
      return this.getSlider(this.uid).total;
    },
  },
  serverPrefetch () {
    return this.loadProducts();
  },
  mounted() {
    if (this.products.length == 0) {
      this.loadProducts();
    }
  },
  methods: {
    loadProducts(current_products) {
      this.loading_products = true;
      current_products = current_products || [];
      var payload = Object.assign(
        {
          per_page: this.per_page,
          current_page: this.products.length / this.per_page + 1,
        },
        this.payload
      );
      return this.$store.dispatch(this.source, payload).then(
        response => {
          this.$store.commit("drugs/MUTATE_SLIDER", {
            slider_id: this.uid,
            data: {
              products: current_products.concat(response.data.results),
              total: response.data.count,
            },
          });
          this.loading_products = false;
        },
        err => {
          this.loading_products = false;
          this.$sentry({ capture: err });
        }
      );
    },
    onSwipeReachEnd() {
      if (this.products.length < this.productsCount) {
        this.loadProducts(this.products);
      }
    },
  },
  fallbackI18n: {
    messages: {
      und: {
        products_slider: {
          see_all: "See all",
          no_results: "No results found",
          error: {
            load_products: "Cannot load products into the slider",
          },
        },
      },
    },
  },
};
</script>

<style lang="scss" scoped>
.swiper-title {
  .strike > span {
    font-size: $font-size-medium * 1.8;
    max-width: 75%;
    line-height: $font-size-medium * 2;
  }

  .strike > span::before,
  .strike > span::after {
    background-color: $corp-color1;
  }

  .link a {
    display: block;
    font-size: $font-size-medium;
    text-decoration: none;
  }
}

.swiper-container {
  margin-top: 1.5rem;
}

.slider-empty {
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  min-height: 26rem;
}

.slider-empty-compact {
  min-height: 21rem;
}

.sw-slide {
  min-height: 25rem;
}

.sw-slide-compact {
  min-height: 20rem;
}

.swiper-slide {
  display: flex;
  justify-content: center;
}

.slider-button {
  background-color: white;
  position: absolute;
  top: 50%;
  z-index: 20;

  &.slider-next {
    right: -20px;
  }

  &.slider-prev {
    left: -20px;
  }

  &.disabled img {
    opacity: 0.3;
  }
}

@include media-breakpoint-down(sm) {
  .sw-slide {
    min-height: 20rem;
  }
}
</style>
